import { useNavigate } from 'react-router-dom';
import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import plusIconTokens from '@amzn/meridian-tokens/base/icon/plus';
import ActionButton from '../components/ActionButton';
import ServicePagination from '../components/ServicePagination';
import { ServiceCardSkeletonList } from '../components/Skeletons';
import ServiceTable from '../components/ServiceTable';
import { useServices } from '../hooks/useServices';

interface ServiceListProps {
  onOpenOnboard: () => void;
}

function ServiceList({ onOpenOnboard }: ServiceListProps) {
  const navigate = useNavigate();
  const { services, isLoading, error, page, hasNextPage, hasPreviousPage, nextPage, previousPage } = useServices();

  return (
    <Column spacing="400" width="100%" minWidth={0}>
      <Row alignmentVertical="center" widths={['fill', 'fit']} wrap="down" spacing="300">
        <Column spacing="100">
          <Heading level={1} type="h500">
            Browse services
          </Heading>
          <Text type="b200" color="secondary">
            Every service with generated MCP specs in the catalog.
          </Text>
        </Column>
        <ActionButton variant="primary" iconTokens={plusIconTokens} onClick={onOpenOnboard}>
          Onboard service
        </ActionButton>
      </Row>

      {error && (
        <Alert type="error" size="large" title="Services failed to load" titleLevel={2}>
          {error}
        </Alert>
      )}

      {(isLoading || services.length > 0) && (
        <ServicePagination
          page={page}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          isLoading={isLoading}
          onNextPage={nextPage}
          onPreviousPage={previousPage}
        />
      )}

      {isLoading && services.length === 0 ? (
        <ServiceCardSkeletonList label="Loading services" />
      ) : (
        services.length > 0 && <ServiceTable services={services} onNavigate={navigate} />
      )}

      {services.length === 0 && !isLoading && !error && (
        <Column alignmentHorizontal="center" spacingInset="600" spacing="300" type="fill">
          <Heading level={2} type="h300">
            No services found
          </Heading>
          <Text type="b200" color="secondary">
            The catalog did not return any services.
          </Text>
        </Column>
      )}
    </Column>
  );
}

export default ServiceList;
